/*
 * Copyright (c) 2018 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Entity, EntityData, EntityFieldKey, EntityModel } from '@typescript-kit/data';

export type TimeRecordType = 'work' | 'comp-time' | 'holiday' | 'public-holiday' | 'sick' | 'break' | 'short-time-work';

export class TimeRecordTypes {
  static readonly WORK: TimeRecordType = 'work';
  static readonly COMP_TIME: TimeRecordType = 'comp-time';
  static readonly HOLIDAY: TimeRecordType = 'holiday';
  static readonly PUBLIC_HOLIDAY: TimeRecordType = 'public-holiday';
  static readonly SICK: TimeRecordType = 'sick';
  static readonly SHORT_TIME_WORK: TimeRecordType = 'short-time-work';
  static readonly BREAK: TimeRecordType = 'break';
}

export class TimeRecordFieldKey extends EntityFieldKey {
  static readonly COMPANY_ID = 'companyId';
  static readonly EMPLOYEE_ID = 'employeeId';
  // static readonly DAY = 'day';
  static readonly DATE = 'date';
  static readonly INDEX = 'index';
  static readonly FROM = 'from';
  static readonly TO = 'to';
  static readonly HOURS = 'hours';
  // static readonly EXPECTED = 'expected';
  static readonly TYPE = 'type';
  static readonly OVERTIME = 'overtime';
  static readonly OVERTIME_50 = 'overtime50';
  static readonly OVERTIME_100 = 'overtime100';
  // static readonly PROJECT = 'project';
  static readonly PROJECT_ID = 'projectId';
  static readonly FROM_LOCATION_ID = 'fromLocationId';
  static readonly TO_LOCATION_ID = 'toLocationId';
  static readonly COMMENT = 'comment';
  static readonly FROM_LONGITUDE = 'fromLongitude';
  static readonly FROM_LATITUDE = 'fromLatitude';
  static readonly TO_LONGITUDE = 'toLongitude';
  static readonly TO_LATITUDE = 'toLatitude';
}

export interface TimeRecordData extends EntityData {
  companyId?: string;
  employeeId?: string;
  date?: string;
  index?: number;
  from?: string;
  to?: string;
  hours?: number;
  // expected?: number;
  type?: TimeRecordType;
  overtime?: number;
  overtime50?: number;
  overtime100?: number;
  // project?: string;
  projectId?: string;
  fromLocationId?: string;
  toLocationId?: string;
  comment?: string;
  fromLongitude?: number;
  fromLatitude?: number;
  toLongitude?: number;
  toLatitude?: number;
}

export class TimeRecord extends Entity implements TimeRecordData {
  readonly companyId: string;
  readonly employeeId: string;
  readonly date: string;
  readonly index: number;
  readonly from: string;
  readonly to: string;
  readonly hours: number;
  // readonly expected: number;
  readonly type: TimeRecordType;
  readonly overtime: number;
  readonly overtime50: number;
  readonly overtime100: number;
  // readonly project: string;
  readonly projectId: string;
  readonly fromLocationId: string;
  readonly toLocationId: string;
  readonly comment: string;
  readonly fromLongitude: number;
  readonly fromLatitude: number;
  readonly toLongitude: number;
  readonly toLatitude: number;

  constructor(data: TimeRecordData) {
    super(data);
    // this.day = data.day;
    this.companyId = data.companyId;
    this.employeeId = data.employeeId;
    this.date = data.date;
    this.index = data.index;
    this.from = data.from;
    this.to = data.to;
    this.hours = data.hours;
    // this.expected = data.expected;
    this.type = data.type;
    this.overtime = data.overtime;
    this.overtime50 = data.overtime50;
    this.overtime100 = data.overtime100;
    // this.project = data.project;
    this.projectId = data.projectId;
    this.fromLocationId = data.fromLocationId;
    this.toLocationId = data.toLocationId;
    this.comment = data.comment;
    this.fromLongitude = data.fromLongitude;
    this.fromLatitude = data.fromLatitude;
    this.toLongitude = data.toLongitude;
    this.toLatitude = data.toLatitude;
  }
}

export class TimeRecordModel extends EntityModel implements TimeRecordData {
  static readonly KEYS = EntityModel.KEYS.concat(
    Object.keys(TimeRecordFieldKey).map((key) => TimeRecordFieldKey[key])
  );

  constructor(data: TimeRecordData) {
    super(data, TimeRecordModel.KEYS);
  }

  get companyId(): string {
    return this.get(TimeRecordFieldKey.COMPANY_ID);
  }

  get employeeId(): string {
    return this.get(TimeRecordFieldKey.EMPLOYEE_ID);
  }

  get date(): string {
    return this.get(TimeRecordFieldKey.DATE);
  }

  get index(): number {
    return this.get(TimeRecordFieldKey.INDEX);
  }

  get from(): string {
    return this.get(TimeRecordFieldKey.FROM);
  }

  set from(value: string) {
    this.set(TimeRecordFieldKey.FROM, value);
  }

  get to(): string {
    return this.get(TimeRecordFieldKey.TO);
  }

  set to(value: string) {
    this.set(TimeRecordFieldKey.TO, value);
  }

  get hours(): number {
    return this.get(TimeRecordFieldKey.HOURS);
  }

  set hours(value: number) {
    this.set(TimeRecordFieldKey.HOURS, value);
  }

  // get expected(): number {
  //   return this.get(TimeRecordFieldKey.EXPECTED);
  // }
  //
  // set expected(value: number) {
  //   this.set(TimeRecordFieldKey.EXPECTED, value);
  // }

  get type(): TimeRecordType {
    return this.get(TimeRecordFieldKey.TYPE, TimeRecordTypes.WORK);
  }

  set type(value: TimeRecordType) {
    this.set(TimeRecordFieldKey.TYPE, value);
  }

  get overtime(): number {
    return this.get(TimeRecordFieldKey.OVERTIME);
  }

  set overtime(value: number) {
    this.set(TimeRecordFieldKey.OVERTIME, value);
  }

  get overtime50(): number {
    return this.get(TimeRecordFieldKey.OVERTIME_50);
  }

  set overtime50(value: number) {
    this.set(TimeRecordFieldKey.OVERTIME_50, value);
  }

  get overtime100(): number {
    return this.get(TimeRecordFieldKey.OVERTIME_100);
  }

  set overtime100(value: number) {
    this.set(TimeRecordFieldKey.OVERTIME_100, value);
  }

  // get project(): string {
  //   return this.get(TimeRecordFieldKey.PROJECT);
  // }
  //
  // set project(value: string) {
  //   this.set(TimeRecordFieldKey.PROJECT, value);
  // }

  get projectId(): string {
    return this.get(TimeRecordFieldKey.PROJECT_ID);
  }

  set projectId(value: string) {
    this.set(TimeRecordFieldKey.PROJECT_ID, value);
  }

  get fromLocationId(): string {
    return this.get(TimeRecordFieldKey.FROM_LOCATION_ID);
  }

  set fromLocationId(value: string) {
    this.set(TimeRecordFieldKey.FROM_LOCATION_ID, value);
  }

  get toLocationId(): string {
    return this.get(TimeRecordFieldKey.TO_LOCATION_ID);
  }

  set toLocationId(value: string) {
    this.set(TimeRecordFieldKey.TO_LOCATION_ID, value);
  }

  get comment(): string {
    return this.get(TimeRecordFieldKey.COMMENT);
  }

  set comment(value: string) {
    this.set(TimeRecordFieldKey.COMMENT, value);
  }

  get fromLongitude(): number {
    return this.get(TimeRecordFieldKey.FROM_LONGITUDE);
  }

  get fromLatitude(): number {
    return this.get(TimeRecordFieldKey.FROM_LATITUDE);
  }

  get toLongitude(): number {
    return this.get(TimeRecordFieldKey.TO_LONGITUDE);
  }

  get toLatitude(): number {
    return this.get(TimeRecordFieldKey.TO_LATITUDE);
  }


  protected setupValues(data: TimeRecordData, exclusive: boolean): TimeRecordData {
    if (exclusive) {
      this.setupDefaultValue(data, TimeRecordFieldKey.DATE, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.INDEX, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.FROM, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.TO, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.HOURS, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.TYPE, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.OVERTIME, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.OVERTIME_50, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.OVERTIME_100, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.PROJECT_ID, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.FROM_LOCATION_ID, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.TO_LOCATION_ID, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.COMMENT, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.FROM_LONGITUDE, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.FROM_LATITUDE, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.TO_LONGITUDE, null);
      this.setupDefaultValue(data, TimeRecordFieldKey.TO_LATITUDE, null);
    }
    return super.setupValues(data, exclusive) as TimeRecordData;
  }

}
